<template>
  <section
    class="dashboard-section quick-menu"
    aria-labelledby="quick-menu-heading"
  >
    <header class="dashboard-section__header">
      <h2 id="quick-menu-heading" class="dashboard-section__title">
        <i class="uil-plus-square" aria-hidden="true"></i>
        {{ $t("general.quick_menu") }}
      </h2>
      <div class="dashboard-section__toolbar">
        <button
          :disabled="!canCustomize"
          aria-controls="customize-dashboard"
          class="dashboard-section__action"
          type="button"
          uk-toggle="target: #customize-dashboard"
        >
          <i class="uil-layer-group" aria-hidden="true"></i>
          {{ $t("general.customize") }}
        </button>
      </div>
    </header>

    <div v-if="items.length" class="quick-menu__grid">
      <button
        v-for="item in items"
        :key="item.id"
        :aria-label="$t(item.key_name)"
        :disabled="!item.allowed || !item.route"
        :title="item.allowed ? '' : $t('general.no_permission')"
        class="quick-menu__card"
        type="button"
        @click="$emit('navigate', item.route)"
      >
        <span v-if="item.badge" class="quick-menu__badge">{{
          item.badge
        }}</span>
        <i :class="item.icon" aria-hidden="true"></i>
        <span class="quick-menu__label">{{ $t(item.key_name) }}</span>
      </button>
    </div>
  </section>
</template>

<script>
export default {
  name: "DashboardQuickMenu",
  props: {
    canCustomize: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
  },
};
</script>

<style scoped>
.dashboard-section {
  margin-bottom: 24px;
}

.dashboard-section__header {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 16px;
}

.dashboard-section__title {
  align-items: center;
  color: #1f2937;
  display: flex;
  font-size: 20px;
  font-weight: 600;
  gap: 8px;
  line-height: 1.4;
  margin: 0;
  min-width: 0;
}

.dashboard-section__action {
  align-items: center;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  color: #374151;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  gap: 4px;
  justify-content: center;
  min-height: 48px;
  padding: 8px 12px;
  text-decoration: none;
  touch-action: manipulation;
}

.dashboard-section__toolbar {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  min-width: 0;
}

.dashboard-section__action:active:not(:disabled) {
  opacity: 0.85;
  transform: scale(0.98);
}

.dashboard-section__action:disabled {
  opacity: 0.5;
  pointer-events: none;
}

.dashboard-section__action:focus-visible {
  outline: 3px solid #2563eb;
  outline-offset: 2px;
}

.quick-menu__grid {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.quick-menu__card {
  align-items: center;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  color: #1f2937;
  display: flex;
  flex-direction: column;
  font-size: 16px;
  font-weight: 600;
  gap: 8px;
  justify-content: center;
  min-height: 112px;
  padding: 16px 12px;
  position: relative;
  text-align: center;
  touch-action: manipulation;
  transition: border-color 160ms ease, box-shadow 160ms ease,
    transform 160ms ease;
  -webkit-tap-highlight-color: transparent;
  width: 100%;
}

.quick-menu__card:focus {
  outline: none;
}

.quick-menu__card:focus-visible {
  box-shadow: 0 0 0 2px rgba(55, 65, 81, 0.28), 0 2px 8px rgba(0, 0, 0, 0.08);
  outline: none;
}

.quick-menu__card:active:not(:disabled) {
  opacity: 0.85;
  transform: scale(0.98);
}

.quick-menu__card:disabled {
  opacity: 0.5;
  pointer-events: none;
}

.quick-menu__card > i {
  color: #2563eb;
  font-size: 28px;
  line-height: 1;
}

.quick-menu__badge {
  background: #c2410c;
  border-radius: 20px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  padding: 4px 8px;
  position: absolute;
  right: 8px;
  top: 8px;
}

.quick-menu__label {
  line-height: 1.4;
  overflow-wrap: anywhere;
}

@media (hover: hover) and (pointer: fine) {
  .dashboard-section__action:not(:disabled),
  .quick-menu__card:not(:disabled) {
    cursor: pointer;
  }

  .quick-menu__card:not(:disabled):hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.14);
    transform: translateY(-2px);
  }
}

@media (min-width: 640px) {
  .quick-menu__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (min-width: 960px) {
  .quick-menu__grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .dashboard-section__header {
    align-items: stretch;
    flex-direction: column;
  }

  .dashboard-section__toolbar {
    align-items: stretch;
    display: flex !important;
    flex: 0 0 auto;
    opacity: 1 !important;
    visibility: visible !important;
    width: 100%;
  }

  .dashboard-section__action {
    width: 100%;
  }

  .quick-menu__grid {
    gap: 12px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .quick-menu__card {
    border-color: #edf0f3;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    gap: 8px;
    min-height: 92px;
    padding: 12px 8px;
  }

  .quick-menu__card > i {
    font-size: 24px;
  }
}

@media (max-width: 359px) {
  .quick-menu__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (prefers-reduced-motion: reduce) {
  .dashboard-section__action:active:not(:disabled),
  .quick-menu__card:not(:disabled):hover,
  .quick-menu__card:active:not(:disabled) {
    transform: none;
  }
}
</style>
